import { Draft } from 'immer'
import {
  type NotificationMode,
  pendingNotificationTargetsAfterUpdate,
} from '@opallabs/concepts'
import { FieldValues } from '@opallabs/satellite'
import { BlockResponse } from '../types'

type CustomFieldData =
  BlockResponse['data']['details']['custom_field_values'][number]['data']
type CustomFieldResponseValue = NonNullable<CustomFieldData>['value']

type UpdateCustomFieldValueParams = {
  fieldValueId: string
  /** Omitted by a mode-only edit, which leaves the value alone. */
  value?: FieldValues | null
  notificationMode?: NotificationMode
  /**
   * Overwrites the pending set outright, which is how Notify empties it the
   * moment the button is pressed rather than a round trip later.
   */
  pendingNotificationTargets?: { user_ids: string[]; group_ids: string[] }
}

export const updateCustomFieldValue = (
  params: UpdateCustomFieldValueParams
) => {
  return (draftDocument: Draft<BlockResponse>) => {
    if (!draftDocument.data) return draftDocument

    const customFieldIndex =
      draftDocument.data.details.custom_field_values.findIndex(
        (field) => field.data?.id === params.fieldValueId
      )
    if (customFieldIndex < 0) return draftDocument

    const existingField =
      draftDocument.data.details.custom_field_values[customFieldIndex]
    const fieldList = draftDocument.data.details.custom_field_values.filter(
      (field) => field.id !== existingField.id
    )

    const previousPending = existingField.data
      ?.pending_notification_targets ?? { user_ids: [], group_ids: [] }
    const { notificationMode, pendingNotificationTargets, value } = params
    const nextValue = value === undefined ? existingField.data?.value : value

    const updatedField = {
      ...existingField,
      // Only what the caller sent moves: a mode-only edit must not clear the
      // picker, and a value-only edit must not invent a mode. An optimistic
      // row still has no mode at all, which the response schema does not model.
      data: {
        ...existingField.data,
        id: params.fieldValueId,
        value: (nextValue ?? null) as CustomFieldResponseValue,
        ...(notificationMode !== undefined && {
          notification_mode: notificationMode,
        }),
        ...(value &&
          'user_ids' in value && {
            pending_notification_targets: pendingNotificationTargetsAfterUpdate(
              {
                previous: {
                  value: existingField.data?.value,
                  pendingTargets: previousPending,
                },
                next: value,
                notificationMode:
                  notificationMode ?? existingField.data?.notification_mode,
              }
            ),
          }),
        ...(pendingNotificationTargets !== undefined && {
          pending_notification_targets: pendingNotificationTargets,
        }),
      } as CustomFieldData,
    }

    fieldList.splice(customFieldIndex, 0, updatedField)
    draftDocument.data.details.custom_field_values = fieldList

    return draftDocument
  }
}
